import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RefreshCw,
  FileArchive,
  AlertCircle,
  RotateCcw,
  Flag,
  Download,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Pagination } from '@/components/shared/Pagination';
import {
  Button,
  PERIODOS,
  HISTORICO_ITENS_POR_PAGINA,
  HistoricoStatusBadge,
  HistoricoProgressBar,
  HistoricoSkeletonRow,
  formatDataHoraHistorico,
  useHistoricoProcessamentos,
} from '../GestaoDocumentosProvider';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cnpj: string;
}

export function HistoricoProcessamentosDialog({ open, onOpenChange, cnpj }: Props) {
  const {
    periodo,
    handlePeriodoChange,
    paginaAtual,
    setPaginaAtual,
    reprocessandoId,
    priorizandoId,
    downloadingId,
    cancelandoId,
    isLoading,
    isFetching,
    refetch,
    isError,
    todosDocumentos,
    totalPaginas,
    documentos,
    inicio,
    handleDownload,
    handlePriorizar,
    handleReprocessar,
    handleCancelar,
  } = useHistoricoProcessamentos(cnpj);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base">Histórico de Processamentos</DialogTitle>
            <div className="flex items-center gap-2 mr-8">
              <Select value={String(periodo)} onValueChange={handlePeriodoChange}>
                <SelectTrigger className="h-8 w-44 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIODOS.map(p => (
                    <SelectItem key={p.value} value={String(p.value)} className="text-xs">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => refetch()}
                disabled={isFetching}
                title="Atualizar"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col min-h-0">
          {isError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
              <AlertCircle className="h-8 w-8 text-red-400" />
              <p>Erro ao carregar o histórico. Tente novamente.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto flex-1 px-4">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="sticky top-0 bg-muted/60 backdrop-blur-sm border-b">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                        Arquivo
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                        Data / Hora
                      </th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                        Status
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">
                        Progresso
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">
                        Aguardando
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">
                        Processando
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">
                        Processados
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">
                        Com Erro
                      </th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {isLoading ? (
                      Array.from({ length: 6 }).map((_, i) => <HistoricoSkeletonRow key={i} />)
                    ) : todosDocumentos.length === 0 ? (
                      <tr>
                        <td colSpan={9}>
                          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
                            <FileArchive className="h-8 w-8 opacity-40" />
                            <p>Nenhum processamento encontrado para o período selecionado.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      documentos.map((item, idx) => (
                        <tr
                          key={item.ID_Upload || idx}
                          className="hover:bg-muted/40 transition-colors"
                        >
                          <td className="px-3 py-3 max-w-[200px]">
                            <span
                              className="truncate block text-foreground font-medium"
                              title={item.NomeArquivo}
                            >
                              {item.NomeArquivo || (
                                <span className="text-muted-foreground italic">—</span>
                              )}
                            </span>
                            {item.Origem && (
                              <span className="text-xs text-muted-foreground">{item.Origem}</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                            {formatDataHoraHistorico(item.DataHoraUpload)}
                          </td>
                          <td className="px-3 py-3">
                            <HistoricoStatusBadge
                              status={String(item.StatusUpload ?? item.Status)}
                            />
                          </td>
                          <td className="px-3 py-3">
                            <HistoricoProgressBar item={item} />
                          </td>
                          <td className="px-3 py-3 text-center tabular-nums">
                            {item.Aguardando ?? '—'}
                          </td>
                          <td className="px-3 py-3 text-center tabular-nums">
                            {item.Processando ?? '—'}
                          </td>
                          <td className="px-3 py-3 text-center tabular-nums text-green-700 font-medium">
                            {item.Processados ?? '—'}
                          </td>
                          <td className="px-3 py-3 text-center tabular-nums">
                            {parseInt(item.ComErro, 10) > 0 ? (
                              <span className="text-red-600 font-medium">{item.ComErro}</span>
                            ) : (
                              <span className="text-muted-foreground">{item.ComErro}</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {item.CaminhoArquivo && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="Download"
                                  disabled={downloadingId === item.ID_Upload}
                                  onClick={() => handleDownload(item)}
                                >
                                  <Download
                                    className={cn(
                                      'h-3.5 w-3.5 text-muted-foreground',
                                      downloadingId === item.ID_Upload && 'animate-pulse'
                                    )}
                                  />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Priorizar"
                                disabled={priorizandoId === item.ID_Upload}
                                onClick={() => handlePriorizar(item)}
                              >
                                <Flag
                                  className={cn(
                                    'h-3.5 w-3.5 text-blue-600',
                                    priorizandoId === item.ID_Upload && 'animate-pulse'
                                  )}
                                />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Reprocessar XML"
                                disabled={reprocessandoId === item.ID_Upload}
                                onClick={() => handleReprocessar(item)}
                              >
                                <RotateCcw
                                  className={cn(
                                    'h-3.5 w-3.5',
                                    reprocessandoId === item.ID_Upload && 'animate-spin'
                                  )}
                                />
                              </Button>
                              {['0', '1', '2'].includes(
                                String(item.StatusUpload ?? item.Status)
                              ) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="Cancelar processamento"
                                  disabled={cancelandoId === item.ID_Upload}
                                  onClick={() => handleCancelar(item)}
                                >
                                  <XCircle
                                    className={cn(
                                      'h-3.5 w-3.5 text-red-500',
                                      cancelandoId === item.ID_Upload && 'animate-pulse'
                                    )}
                                  />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20 shrink-0">
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {todosDocumentos.length === 0
                    ? 'Nenhum item'
                    : `${inicio + 1}–${Math.min(inicio + HISTORICO_ITENS_POR_PAGINA, todosDocumentos.length)} de ${todosDocumentos.length}`}
                </span>
                <Pagination
                  currentPage={paginaAtual}
                  totalPages={totalPaginas}
                  onPageChange={setPaginaAtual}
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
